import { SubjectRawRule, ExtractSubjectType, PureAbility } from "@casl/ability";
import { unpackRules, PackRule } from "@casl/ability/extra";
import type { AppSubjects } from "@lbcrm-api/casl/app.subjects";
import axios, { Axios, isAxiosError } from "axios";
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Action } from "src/casl/Action";
import { AblePerson, AuthenticationContext } from "./AuthenticationContext";
import { AbilityContext } from "./Can";
import { API_URL } from "src/core/constants";
import { toast } from "react-toastify";
import { LoadingScreen } from "src/core/components/LoadingScreen";
import { PrismaQuery, createPrismaAbility } from "@casl/prisma";
import { useAuthToken } from "../useAuthToken";
import { releaseVersion } from "src/core/service-worker/releaseVersion";
import { usePostHog } from "posthog-js/react";
import { apiErrorMessage } from "src/core/utilities/apiErrorMessage";
import { rememberSignInHandOffRefusal, takeSignInHandOffCode } from "../signInHandOff";

const HAND_OFF_REFUSED = "That sign-in link has been used or has expired. Sign in with your mobile.";

interface Props {}

export const createAxios = () => {
    return axios.create({
        baseURL: API_URL,
        headers: {
            "X-Client-Version": releaseVersion,
        },
    });
};

export const defaultAxios = createAxios();

/**
 * Session tokens expire, so a 401 arriving while one is held means the session
 * is over rather than something being broken. Say so before bouncing to the
 * login screen, which otherwise looks like the app losing the user's place. The
 * server's own reason wins where it gave one, such as a login being disabled.
 */
const SESSION_ENDED_MESSAGE = "Your session has ended. Sign in again to carry on.";

const sessionEndedMessage = (error: unknown) => {
    const message = isAxiosError(error) ? (error.response?.data as { message?: unknown } | undefined)?.message : undefined;

    return typeof message === "string" && message !== "Unauthorized" ? message : SESSION_ENDED_MESSAGE;
};

/**
 * Swapping the token each time the app opens is what keeps a session going for as long as it is
 * used. Any failure keeps the token held, so a refusal surfaces through the profile request and a
 * dropped connection signs nobody out.
 */
const refreshedToken = async (token: string) => {
    try {
        const response = await axios.post<{ token: string }>(API_URL + "/auth/session/refresh", undefined, {
            headers: {
                Authorization: "Bearer " + token,

                "X-Client-Version": import.meta.env.VITE_RELEASE_VERSION || "dev",
            },
        });

        return response.data.token || token;
    } catch {
        return token;
    }
};

export const AuthenticationProvider = (props: PropsWithChildren<Props>) => {
    const posthog = usePostHog();

    const [initialised, setInitialised] = useState(false);
    const initialising = useRef(false);
    const [person, setPerson] = useState<AblePerson | null>();
    const [hasDuties, setHasDuties] = useState(false);
    const [ability, setAbility] = useState<PureAbility<[Action, AppSubjects], PrismaQuery>>();
    const [authenticationError, setAuthenticationError] = useState<Error>();

    const [authToken, setAuthToken] = useAuthToken();

    const axiosRef = useRef<Axios | undefined>(defaultAxios);

    const navigate = useNavigate();

    const logout = useCallback(
        async (recordFromState = false) => {
            setAuthToken(null);

            const from = encodeURIComponent(window.location.pathname + window.location.search);

            navigate("/login" + (recordFromState ? `?from=${from}` : ""));

            setPerson(undefined);
            setHasDuties(false);
            setAuthenticationError(undefined);

            axiosRef.current = undefined;

            // A registrar's phone at the door is signed into by one person after another
            try {
                posthog.reset();
            } catch {
                // Do nothing if PostHog fails
            }
        },
        [navigate, posthog, setAuthToken]
    );

    const getProfile = useCallback(
        async (token: string) => {
            try {
                const response = await axios.get<{
                    person?: AblePerson;
                    hasDuties: boolean;
                    rules: PackRule<SubjectRawRule<Action, ExtractSubjectType<AppSubjects>, PrismaQuery>>[];
                }>(API_URL + "/profile", {
                    headers: {
                        Authorization: "Bearer " + token,

                        "X-Client-Version": releaseVersion,
                    },
                });

                const person = response.data.person;
                const rules = response.data.rules;

                axiosRef.current = createAxios();

                axiosRef.current.interceptors.request.use(
                    async (config) => {
                        if (config.headers) {
                            config.headers["Authorization"] = "Bearer " + token;
                        }

                        return config;
                    },
                    (error) => {
                        return Promise.reject(error);
                    }
                );

                axiosRef.current.interceptors.response.use(
                    (response) => {
                        return response;
                    },
                    async (error) => {
                        // Sign out if a 401 was received
                        if (isAxiosError(error) && error.response?.status === 401) {
                            toast.info(sessionEndedMessage(error));

                            await logout(true);
                        }

                        return Promise.reject(error);
                    }
                );

                setPerson(person);
                setHasDuties(response.data.hasDuties);

                const ability = createPrismaAbility<[Action, AppSubjects], PrismaQuery>(unpackRules(rules));

                // @ts-expect-error - missing private properties
                setAbility(ability);

                // The id alone, as PostHog is never sent a name, email or phone number
                if (posthog && person) {
                    posthog.identify(person.id);
                }
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    if (error.response?.status === 401) {
                        toast.info(sessionEndedMessage(error));

                        await logout(true);
                    }

                    setAuthenticationError(error);
                } else if (error instanceof Error) {
                    setAuthenticationError(error);
                } else {
                    setAuthenticationError(
                        new Error(
                            "An unknown error occurred while logging in. Please try again shortly, or contact someone at church."
                        )
                    );
                }
            }
        },
        [logout, posthog]
    );

    // Attempts to authenticate the user using the token in local storage
    useEffect(() => {
        if (initialised || initialising.current) {
            return;
        }

        async function initialise() {
            try {
                const handOffCode = takeSignInHandOffCode();

                if (handOffCode) {
                    try {
                        const { data } = await createAxios().post<{ token: string }>("/auth/hand-off", {
                            code: handOffCode,
                        });

                        setAuthToken(data.token);
                        await getProfile(data.token);

                        return;
                    } catch (error) {
                        rememberSignInHandOffRefusal(apiErrorMessage(error, HAND_OFF_REFUSED));
                    }
                }

                if (authToken) {
                    const token = await refreshedToken(authToken);

                    if (token !== authToken) {
                        setAuthToken(token);
                    }

                    await getProfile(token);
                }
            } catch {
                // TODO handle any initialisation error. This should probably clear the screen
            } finally {
                setInitialised(true);
                initialising.current = false;
            }
        }

        initialising.current = true;
        initialise();
    }, [initialised, getProfile, authToken, setAuthToken]);

    const handleLogin = async (newToken: string) => {
        if (!newToken) {
            toast.error("No authentication token provided. Please try logging in again or contact church for help.");
            return;
        }

        setAuthToken(newToken);

        try {
            await getProfile(newToken);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                setAuthenticationError(error);
            } else if (error instanceof Error) {
                setAuthenticationError(error);
            } else {
                setAuthenticationError(
                    new Error(
                        "An unknown error occurred while logging in. Please try again shortly, or contact someone at church."
                    )
                );
            }
        }
    };

    const refreshProfile = async () => {
        try {
            if (!authToken) {
                toast.error("You are not authenticated. Please log in again.");

                return;
            }

            await getProfile(authToken);
        } catch {
            toast.error("An error occurred while refreshing your profile. Please try again shortly.");
        }
    };

    if (!initialised) {
        return <LoadingScreen img />;
    }

    return (
        <AuthenticationContext.Provider
            value={{
                person,
                hasDuties,
                error: authenticationError,
                client: axiosRef.current,
                handleLogin,
                logout,
                refreshProfile,
            }}
        >
            {ability ? (
                <AbilityContext.Provider value={ability}>{props.children}</AbilityContext.Provider>
            ) : (
                props.children
            )}
        </AuthenticationContext.Provider>
    );
};
